import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../utils/api';

// ── Story Gift Orders ─────────────────────────────────────────────────────────
const STORY_STATUS_COLOR = { pending: 'secondary', paid: 'info', generating: 'warning', delivered: 'success', failed: 'danger' };
const STORY_STATUS_EMOJI = { pending: '⏳', paid: '💳', generating: '⚙️', delivered: '✅', failed: '❌' };

const StoryGiftsTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders]     = useState([]);
  const [total, setTotal]       = useState(0);
  const [pages, setPages]       = useState(1);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving]     = useState(false);

  const status = searchParams.get('status') || '';
  const search = searchParams.get('search') || '';
  const page   = parseInt(searchParams.get('page') || '1');
  const [searchInput, setSearchInput] = useState(search);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 20 };
    if (status) params.status = status;
    if (search) params.search = search;
    API.get('/admin/gifts', { params })
      .then(r => {
        setOrders(r.data.orders);
        setTotal(r.data.total);
        setPages(r.data.pages);
        setStatusCounts(r.data.statusCounts || {});
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [status, search, page]);

  useEffect(() => { load(); }, [load]);

  const set = (key, val) => {
    const p = Object.fromEntries(searchParams);
    if (val) p[key] = val; else delete p[key];
    if (key !== 'page') p.page = '1';
    setSearchParams(p);
  };

  const openOrder = async (o) => {
    const r = await API.get(`/admin/gifts/${o._id}`);
    setSelected(r.data.order);
  };

  const updateStatus = async (newStatus) => {
    setSaving(true);
    try {
      await API.patch(`/admin/gifts/${selected._id}`, { status: newStatus });
      const r = await API.get(`/admin/gifts/${selected._id}`);
      setSelected(r.data.order);
      load();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <>
      {/* Status tabs */}
      <div className="d-flex gap-2 flex-wrap mb-4">
        {[
          { val: '', label: 'All', count: total },
          { val: 'pending',    label: 'Pending',    count: statusCounts.pending },
          { val: 'paid',       label: 'Paid',       count: statusCounts.paid },
          { val: 'generating', label: 'Generating', count: statusCounts.generating },
          { val: 'delivered',  label: 'Delivered',  count: statusCounts.delivered },
          { val: 'failed',     label: 'Failed',     count: statusCounts.failed },
        ].map(tab => (
          <button key={tab.val} onClick={() => set('status', tab.val)}
            className={`btn btn-sm rounded-pill px-3 ${status === tab.val ? 'btn-dream' : 'btn-outline-secondary'}`}
            style={{ fontSize: '13px' }}>
            {tab.label}
            {tab.count > 0 && <span className="badge rounded-pill ms-1"
              style={{ background: status === tab.val ? 'rgba(255,255,255,0.3)' : 'var(--header-bg)', color: status === tab.val ? '#fff' : 'var(--text-body)', fontSize: '10px' }}>
              {tab.count}
            </span>}
          </button>
        ))}
        <form className="ms-auto d-flex gap-2" onSubmit={e => { e.preventDefault(); set('search', searchInput); }}>
          <input className="form-control form-control-sm" placeholder="Search name or email…" value={searchInput}
            onChange={e => setSearchInput(e.target.value)} style={{ borderRadius: '8px', width: '200px' }} />
          <button type="submit" className="btn btn-sm btn-outline-secondary rounded-pill">
            <i className="bi bi-search"></i>
          </button>
        </form>
      </div>

      <div className="row g-3">
        <div className={selected ? 'col-lg-6' : 'col-12'}>
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
            </div>
          ) : (
            <>
              <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead style={{ background: 'var(--page-bg)' }}>
                      <tr>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>FROM</th>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>TO</th>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>CHILD</th>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>STATUS</th>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>DATE</th>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.length === 0 ? (
                        <tr><td colSpan={6} className="text-center text-muted py-5">No story gift orders found</td></tr>
                      ) : orders.map(o => (
                        <tr key={o._id} style={{ verticalAlign: 'middle', cursor: 'pointer', background: selected?._id === o._id ? '#f8f5ff' : undefined }}
                          onClick={() => openOrder(o)}>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '13px' }}>
                            <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>{o.senderName}</div>
                            <div style={{ color: 'var(--text-muted2)', fontSize: '12px' }}>{o.senderEmail}</div>
                          </td>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '13px', color: 'var(--text-muted2)' }}>
                            {o.recipientEmail}
                          </td>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '13px', color: 'var(--text-main)' }}>
                            {o.childName}, {o.childAge}y
                          </td>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                            <span className={`badge bg-${STORY_STATUS_COLOR[o.status]} bg-opacity-10 text-${STORY_STATUS_COLOR[o.status]}`} style={{ fontSize: '11px' }}>
                              {STORY_STATUS_EMOJI[o.status]} {o.status}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '12px', color: 'var(--text-muted2)' }}>
                            {new Date(o.createdAt).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                            <button className="btn btn-sm btn-outline-secondary rounded-pill px-2" style={{ fontSize: '12px' }}>View</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {pages > 1 && (
                <div className="d-flex justify-content-center gap-2 mt-3">
                  <button className="btn btn-sm btn-outline-secondary rounded-pill" disabled={page <= 1}
                    onClick={() => set('page', String(page - 1))}>← Prev</button>
                  <span className="btn btn-sm disabled">{page} / {pages}</span>
                  <button className="btn btn-sm btn-outline-secondary rounded-pill" disabled={page >= pages}
                    onClick={() => set('page', String(page + 1))}>Next →</button>
                </div>
              )}
            </>
          )}
        </div>

        {selected && (
          <div className="col-lg-6">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', position: 'sticky', top: '72px' }}>
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <h6 className="fw-bold mb-0" style={{ color: 'var(--text-main)' }}>Story Gift Detail</h6>
                  <button onClick={() => setSelected(null)} className="btn btn-sm btn-outline-secondary rounded-pill px-2" style={{ fontSize: '12px' }}>
                    <i className="bi bi-x"></i>
                  </button>
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-6 p-3 rounded-3" style={{ background: 'var(--page-bg)' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted2)', marginBottom: '2px' }}>FROM</div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-main)' }}>{selected.senderName}</div>
                    <a href={`mailto:${selected.senderEmail}`} style={{ fontSize: '12px', color: '#6f42c1' }}>{selected.senderEmail}</a>
                  </div>
                  <div className="col-6 p-3 rounded-3" style={{ background: 'var(--green-section)' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted2)', marginBottom: '2px' }}>TO</div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-main)' }}>{selected.recipientName || 'Not specified'}</div>
                    <a href={`mailto:${selected.recipientEmail}`} style={{ fontSize: '12px', color: '#059669' }}>{selected.recipientEmail}</a>
                  </div>
                </div>

                <div className="p-3 rounded-3 mb-3" style={{ background: 'var(--orange-section)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted2)', marginBottom: '4px' }}>CHILD DETAILS</div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-main)' }}>{selected.childName}, Age {selected.childAge}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>Interests: {selected.childInterests?.join(', ') || 'None'}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>Language: {selected.language || 'en'}</div>
                </div>

                {selected.personalMessage && (
                  <div className="p-3 rounded-3 mb-3" style={{ background: 'var(--page-bg)' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted2)', marginBottom: '4px' }}>PERSONAL MESSAGE</div>
                    <div style={{ fontSize: '13px', color: 'var(--text-body)', fontStyle: 'italic' }}>"{selected.personalMessage}"</div>
                  </div>
                )}

                {selected.storyTitle && (
                  <div className="p-3 rounded-3 mb-3" style={{ background: '#f0f9ff', border: '1px solid #bae6fd' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted2)', marginBottom: '4px' }}>GENERATED STORY</div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-main)' }}>{selected.storyTitle}</div>
                    {selected.storyMoralLesson && <div style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>Moral: {selected.storyMoralLesson}</div>}
                    {selected.deliveredAt && <div style={{ fontSize: '11px', color: '#059669' }}>Delivered {new Date(selected.deliveredAt).toLocaleString()}</div>}
                  </div>
                )}

                <div className="mb-3">
                  <div className="fw-semibold mb-2" style={{ fontSize: '13px', color: 'var(--text-main)' }}>Update Status</div>
                  <div className="d-flex gap-2 flex-wrap">
                    {['pending','paid','generating','delivered','failed'].map(s => (
                      <button key={s} disabled={saving || selected.status === s}
                        onClick={() => updateStatus(s)}
                        className={`btn btn-sm rounded-pill px-2 ${selected.status === s ? `btn-${STORY_STATUS_COLOR[s]}` : `btn-outline-${STORY_STATUS_COLOR[s]}`}`}
                        style={{ fontSize: '11px' }}>
                        {STORY_STATUS_EMOJI[s]} {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <a href={`mailto:${selected.senderEmail}`} className="btn btn-sm btn-outline-secondary rounded-pill flex-fill text-center" style={{ fontSize: '12px' }}>
                    <i className="bi bi-envelope me-1"></i>Email Sender
                  </a>
                  <a href={`mailto:${selected.recipientEmail}`} className="btn btn-sm btn-outline-secondary rounded-pill flex-fill text-center" style={{ fontSize: '12px' }}>
                    <i className="bi bi-envelope me-1"></i>Email Recipient
                  </a>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-soft)', marginTop: '12px' }}>Stripe Session: {selected.stripeSessionId}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// ── Gift Subscription Orders ──────────────────────────────────────────────────
const SUB_STATUS_COLOR = { pending: 'secondary', paid: 'info', sent: 'primary', redeemed: 'success', expired: 'warning', failed: 'danger' };
const SUB_STATUS_EMOJI = { pending: '⏳', paid: '💳', sent: '📧', redeemed: '✅', expired: '⌛', failed: '❌' };
const PLAN_COLOR = { basic: '#6f42c1', family: '#059669' };
const PLAN_BG    = { basic: '#f0ebff', family: '#f0fdf4' };

const SubGiftsTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [subs, setSubs]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [pages, setPages]       = useState(1);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving]     = useState(false);

  const status = searchParams.get('substatus') || '';
  const search = searchParams.get('subsearch') || '';
  const page   = parseInt(searchParams.get('subpage') || '1');
  const [searchInput, setSearchInput] = useState(search);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 20 };
    if (status) params.status = status;
    if (search) params.search = search;
    API.get('/admin/gift-subscriptions', { params })
      .then(r => {
        setSubs(r.data.subscriptions);
        setTotal(r.data.total);
        setPages(r.data.pages);
        setStatusCounts(r.data.statusCounts || {});
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [status, search, page]);

  useEffect(() => { load(); }, [load]);

  const set = (key, val) => {
    const p = Object.fromEntries(searchParams);
    if (val) p[key] = val; else delete p[key];
    p.subpage = '1';
    setSearchParams(p);
  };

  const openSub = async (s) => {
    const r = await API.get(`/admin/gift-subscriptions/${s._id}`);
    setSelected(r.data.subscription);
  };

  const updateStatus = async (newStatus) => {
    setSaving(true);
    try {
      const r = await API.patch(`/admin/gift-subscriptions/${selected._id}`, { status: newStatus });
      setSelected(r.data.subscription);
      load();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <>
      {/* Status tabs */}
      <div className="d-flex gap-2 flex-wrap mb-4">
        {[
          { val: '', label: 'All', count: total },
          { val: 'pending',  label: 'Pending',  count: statusCounts.pending },
          { val: 'paid',     label: 'Paid',     count: statusCounts.paid },
          { val: 'sent',     label: 'Sent',     count: statusCounts.sent },
          { val: 'redeemed', label: 'Redeemed', count: statusCounts.redeemed },
          { val: 'expired',  label: 'Expired',  count: statusCounts.expired },
          { val: 'failed',   label: 'Failed',   count: statusCounts.failed },
        ].map(tab => (
          <button key={tab.val} onClick={() => set('substatus', tab.val)}
            className={`btn btn-sm rounded-pill px-3 ${status === tab.val ? 'btn-dream' : 'btn-outline-secondary'}`}
            style={{ fontSize: '13px' }}>
            {tab.label}
            {tab.count > 0 && <span className="badge rounded-pill ms-1"
              style={{ background: status === tab.val ? 'rgba(255,255,255,0.3)' : 'var(--header-bg)', color: status === tab.val ? '#fff' : 'var(--text-body)', fontSize: '10px' }}>
              {tab.count}
            </span>}
          </button>
        ))}
        <form className="ms-auto d-flex gap-2" onSubmit={e => { e.preventDefault(); set('subsearch', searchInput); }}>
          <input className="form-control form-control-sm" placeholder="Search name or email…" value={searchInput}
            onChange={e => setSearchInput(e.target.value)} style={{ borderRadius: '8px', width: '200px' }} />
          <button type="submit" className="btn btn-sm btn-outline-secondary rounded-pill">
            <i className="bi bi-search"></i>
          </button>
        </form>
      </div>

      <div className="row g-3">
        <div className={selected ? 'col-lg-6' : 'col-12'}>
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
            </div>
          ) : (
            <>
              <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead style={{ background: 'var(--page-bg)' }}>
                      <tr>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>FROM</th>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>TO</th>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>PLAN</th>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>DURATION</th>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>STATUS</th>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>DATE</th>
                        <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {subs.length === 0 ? (
                        <tr><td colSpan={7} className="text-center text-muted py-5">No subscription gifts found</td></tr>
                      ) : subs.map(s => (
                        <tr key={s._id} style={{ verticalAlign: 'middle', cursor: 'pointer', background: selected?._id === s._id ? '#f8f5ff' : undefined }}
                          onClick={() => openSub(s)}>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '13px' }}>
                            <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>{s.senderName}</div>
                            <div style={{ color: 'var(--text-muted2)', fontSize: '12px' }}>{s.senderEmail}</div>
                          </td>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '13px' }}>
                            <div style={{ color: 'var(--text-main)' }}>{s.recipientName || '—'}</div>
                            <div style={{ color: 'var(--text-muted2)', fontSize: '12px' }}>{s.recipientEmail}</div>
                          </td>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                            <span className="badge rounded-pill" style={{ background: PLAN_BG[s.plan], color: PLAN_COLOR[s.plan], fontSize: '11px' }}>
                              {s.plan}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '13px', color: 'var(--text-body)' }}>
                            {s.durationMonths} mo
                          </td>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                            <span className={`badge bg-${SUB_STATUS_COLOR[s.status]} bg-opacity-10 text-${SUB_STATUS_COLOR[s.status]}`} style={{ fontSize: '11px' }}>
                              {SUB_STATUS_EMOJI[s.status]} {s.status}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '12px', color: 'var(--text-muted2)' }}>
                            {new Date(s.createdAt).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                            <button className="btn btn-sm btn-outline-secondary rounded-pill px-2" style={{ fontSize: '12px' }}>View</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {pages > 1 && (
                <div className="d-flex justify-content-center gap-2 mt-3">
                  <button className="btn btn-sm btn-outline-secondary rounded-pill" disabled={page <= 1}
                    onClick={() => set('subpage', String(page - 1))}>← Prev</button>
                  <span className="btn btn-sm disabled">{page} / {pages}</span>
                  <button className="btn btn-sm btn-outline-secondary rounded-pill" disabled={page >= pages}
                    onClick={() => set('subpage', String(page + 1))}>Next →</button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="col-lg-6">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', position: 'sticky', top: '72px' }}>
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <h6 className="fw-bold mb-0" style={{ color: 'var(--text-main)' }}>Gift Subscription Detail</h6>
                  <button onClick={() => setSelected(null)} className="btn btn-sm btn-outline-secondary rounded-pill px-2" style={{ fontSize: '12px' }}>
                    <i className="bi bi-x"></i>
                  </button>
                </div>

                {/* Sender / Recipient */}
                <div className="row g-2 mb-3">
                  <div className="col-6 p-3 rounded-3" style={{ background: 'var(--page-bg)' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted2)', marginBottom: '2px' }}>FROM</div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-main)' }}>{selected.senderName}</div>
                    <a href={`mailto:${selected.senderEmail}`} style={{ fontSize: '12px', color: '#6f42c1' }}>{selected.senderEmail}</a>
                  </div>
                  <div className="col-6 p-3 rounded-3" style={{ background: 'var(--green-section)' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted2)', marginBottom: '2px' }}>TO</div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-main)' }}>{selected.recipientName || '—'}</div>
                    <a href={`mailto:${selected.recipientEmail}`} style={{ fontSize: '12px', color: '#059669' }}>{selected.recipientEmail}</a>
                  </div>
                </div>

                {/* Plan & Duration */}
                <div className="p-3 rounded-3 mb-3 d-flex gap-3" style={{ background: 'var(--orange-section)' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted2)' }}>PLAN</div>
                    <span className="badge rounded-pill mt-1" style={{ background: PLAN_BG[selected.plan], color: PLAN_COLOR[selected.plan], fontSize: '13px', padding: '4px 10px' }}>
                      {selected.plan?.charAt(0).toUpperCase() + selected.plan?.slice(1)}
                    </span>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted2)' }}>DURATION</div>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-main)' }}>{selected.durationMonths} month{selected.durationMonths > 1 ? 's' : ''}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted2)' }}>STATUS</div>
                    <span className={`badge bg-${SUB_STATUS_COLOR[selected.status]} bg-opacity-10 text-${SUB_STATUS_COLOR[selected.status]} mt-1`} style={{ fontSize: '12px' }}>
                      {SUB_STATUS_EMOJI[selected.status]} {selected.status}
                    </span>
                  </div>
                </div>

                {/* Redemption info */}
                {selected.redeemedBy && (
                  <div className="p-3 rounded-3 mb-3" style={{ background: 'var(--green-section)', border: '1px solid #bbf7d0' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted2)', marginBottom: '4px' }}>REDEEMED BY</div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-main)' }}>{selected.redeemedBy.name}</div>
                    <div style={{ fontSize: '12px', color: '#059669' }}>{selected.redeemedBy.email}</div>
                    {selected.redeemedAt && <div style={{ fontSize: '11px', color: 'var(--text-muted2)', marginTop: '2px' }}>
                      {new Date(selected.redeemedAt).toLocaleString()}
                    </div>}
                  </div>
                )}

                {/* Token expiry */}
                {selected.tokenExpiresAt && (
                  <div className="mb-3 p-2 rounded-3" style={{ background: 'var(--header-bg)', fontSize: '12px', color: 'var(--text-muted2)' }}>
                    <i className="bi bi-clock me-1"></i>
                    Token expires: {new Date(selected.tokenExpiresAt).toLocaleDateString()}
                    {new Date(selected.tokenExpiresAt) < new Date() && (
                      <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '10px' }}>Expired</span>
                    )}
                  </div>
                )}

                {selected.personalMessage && (
                  <div className="p-3 rounded-3 mb-3" style={{ background: 'var(--page-bg)' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted2)', marginBottom: '4px' }}>PERSONAL MESSAGE</div>
                    <div style={{ fontSize: '13px', color: 'var(--text-body)', fontStyle: 'italic' }}>"{selected.personalMessage}"</div>
                  </div>
                )}

                {/* Status update */}
                <div className="mb-3">
                  <div className="fw-semibold mb-2" style={{ fontSize: '13px', color: 'var(--text-main)' }}>Update Status</div>
                  <div className="d-flex gap-2 flex-wrap">
                    {['pending','paid','sent','redeemed','expired','failed'].map(s => (
                      <button key={s} disabled={saving || selected.status === s}
                        onClick={() => updateStatus(s)}
                        className={`btn btn-sm rounded-pill px-2 ${selected.status === s ? `btn-${SUB_STATUS_COLOR[s]}` : `btn-outline-${SUB_STATUS_COLOR[s]}`}`}
                        style={{ fontSize: '11px' }}>
                        {SUB_STATUS_EMOJI[s]} {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <a href={`mailto:${selected.senderEmail}`} className="btn btn-sm btn-outline-secondary rounded-pill flex-fill text-center" style={{ fontSize: '12px' }}>
                    <i className="bi bi-envelope me-1"></i>Email Sender
                  </a>
                  <a href={`mailto:${selected.recipientEmail}`} className="btn btn-sm btn-outline-secondary rounded-pill flex-fill text-center" style={{ fontSize: '12px' }}>
                    <i className="bi bi-envelope me-1"></i>Email Recipient
                  </a>
                </div>

                <div style={{ fontSize: '11px', color: 'var(--text-soft)', marginTop: '12px' }}>
                  Stripe Session: {selected.stripeSessionId}
                  {selected.token && <><br />Token: <code style={{ fontSize: '10px' }}>{selected.token.substring(0, 20)}…</code></>}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const AdminGiftOrders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'stories';

  const setTab = (t) => {
    setSearchParams({ tab: t });
  };

  return (
    <AdminLayout title="Gift Orders">
      {/* Tab switcher */}
      <div className="d-flex gap-2 mb-4">
        <button
          onClick={() => setTab('stories')}
          className={`btn btn-sm rounded-pill px-4 ${tab === 'stories' ? 'btn-dream' : 'btn-outline-secondary'}`}
          style={{ fontSize: '14px', fontWeight: 600 }}>
          📖 Story Gifts
        </button>
        <button
          onClick={() => setTab('subscriptions')}
          className={`btn btn-sm rounded-pill px-4 ${tab === 'subscriptions' ? 'btn-dream' : 'btn-outline-secondary'}`}
          style={{ fontSize: '14px', fontWeight: 600 }}>
          🎁 Subscription Gifts
        </button>
      </div>

      {tab === 'stories' ? <StoryGiftsTab /> : <SubGiftsTab />}
    </AdminLayout>
  );
};

export default AdminGiftOrders;
